import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import {
  getIsAddTeamLocationLoading,
  getIsAddTeamLocationModalOpen,
} from '@artemis/store/teams/selectors';
import {
  addTeamLocation,
  setAddTeamLocationModalOpen,
} from '@artemis/store/teams/slice';
import StandardPrompt from '@artemis/components/StandardPrompt';
import { FormattedMessage } from '@artemis/integrations/contentful/utils';
import LocationAutocomplete from './LocationAutocomplete';
import {
  logAddLocationModalImpression,
  logAddLocationModalSubmitClick,
} from './analytics';

const Container = styled.div`
  margin-top: 16px;
  min-height: 400px;
`;

const AddLocationModal = ({ teamId, email, onLocationConfirmed }) => {
  const dispatch = useDispatch();

  const isOpen = useSelector(getIsAddTeamLocationModalOpen);
  const isLoading = useSelector(getIsAddTeamLocationLoading);

  const [selectedLocation, setSelectedLocation] = useState();

  useEffect(() => {
    if (isOpen) {
      logAddLocationModalImpression({ teamId });
      setSelectedLocation(null);
    }
  }, [isOpen]);

  const onClose = () => {
    dispatch(setAddTeamLocationModalOpen(false));
  };

  const onConfirm = () => {
    if (selectedLocation) {
      const { googlePlaceId } = selectedLocation;
      logAddLocationModalSubmitClick({ teamId, googlePlaceId });
      dispatch(
        addTeamLocation({
          teamId,
          googlePlaceId,
          workEmail: email,
          onSuccess: location => onLocationConfirmed(location),
        }),
      );
    }
  };

  return (
    <StandardPrompt
      title={<FormattedMessage entry="teams.addLocationModal.title" />}
      content={
        <Container>
          <LocationAutocomplete
            onLocationSelected={setSelectedLocation}
            selectedGooglePlaceId={selectedLocation?.googlePlaceId}
          />
        </Container>
      }
      primaryButtonText={
        <FormattedMessage entry="teams.addLocationModal.primaryButton" />
      }
      secondaryButtonText={<FormattedMessage entry="global.cancel" />}
      onClose={onClose}
      isOpen={isOpen}
      onClickPrimaryButton={onConfirm}
      onClickSecondaryButton={onClose}
      isPrimaryButtonDisabled={!selectedLocation}
      isLoading={isLoading}
      isClosable={false}
      mobileFullHeight
    />
  );
};

AddLocationModal.propTypes = {
  teamId: PropTypes.string,
  email: PropTypes.string,
  onLocationConfirmed: PropTypes.func.isRequired,
};

export default AddLocationModal;
